import { HttpClient, httpResource } from '@angular/common/http';
import { computed, inject, Injectable, signal } from '@angular/core';
import { Observable } from 'rxjs';
import type {
  AuditActivityResponse,
  DashboardStats,
  EmployeeDashboard,
  OperationBreakdown,
  PlanningDashboardMetrics,
  RecentChangesResponse,
  SchemaBreakdown,
} from '@zag/core/models';

/**
 * Flags controlling which dashboard resource categories should be loaded.
 * The component sets these based on the current user's permissions to avoid
 * firing HTTP requests that would result in 403 responses.
 */
export interface DashboardResourceFlags {
  /** Load admin stats (user count, person count) */
  stats: boolean;
  /** Load audit activity, recent changes, operation/schema breakdowns */
  audit: boolean;
  /** Load planning metrics (shifts today, coverage, trends) */
  planning: boolean;
  /** Load employee self-service dashboard (my shifts) */
  employee: boolean;
}

@Injectable({ providedIn: 'root' })
export class DashboardService {
  private readonly http = inject(HttpClient);
  private readonly basePath = '/api/v1/dashboard';

  /**
   * Resource category flags — set by the component based on user permissions.
   * Defaults to all-disabled so no requests fire until the component opts in.
   */
  readonly enabledResources = signal<DashboardResourceFlags>({
    stats: false,
    audit: false,
    planning: false,
    employee: false,
  });

  /** Signal to trigger refresh of all dashboard data */
  private readonly refreshTrigger = signal(0);

  /** Configuration for audit activity query */
  readonly activityDays = signal(30);

  /** Configuration for recent changes query */
  readonly recentLimit = signal(10);

  /** Selected Monday week start for employee dashboard widgets */
  readonly employeeWeekStart = signal(getWeekStartIso(new Date()));

  // HTTP Resources

  /** Stats resource (user count, person count) — gated by `stats` flag */
  private readonly statsResource = httpResource<DashboardStats>(() => {
    this.refreshTrigger();
    return this.enabledResources().stats ? `${this.basePath}/stats` : undefined;
  });

  /** Audit activity timeline resource — gated by `audit` flag */
  private readonly activityResource = httpResource<AuditActivityResponse>(() => {
    this.refreshTrigger();
    return this.enabledResources().audit ? `${this.basePath}/audit/activity?days=${this.activityDays()}` : undefined;
  });

  /** Recent changes resource — gated by `audit` flag */
  private readonly recentChangesResource = httpResource<RecentChangesResponse>(() => {
    this.refreshTrigger();
    return this.enabledResources().audit ? `${this.basePath}/audit/recent?limit=${this.recentLimit()}` : undefined;
  });

  /** Operation breakdown resource — gated by `audit` flag */
  private readonly operationBreakdownResource = httpResource<OperationBreakdown[]>(() => {
    this.refreshTrigger();
    return this.enabledResources().audit ? `${this.basePath}/audit/by-operation?days=${this.activityDays()}` : undefined;
  });

  /** Schema breakdown resource — gated by `audit` flag */
  private readonly schemaBreakdownResource = httpResource<SchemaBreakdown[]>(() => {
    this.refreshTrigger();
    return this.enabledResources().audit ? `${this.basePath}/audit/by-schema?days=${this.activityDays()}` : undefined;
  });

  /** Planning dashboard metrics resource — gated by `planning` flag */
  private readonly planningMetricsResource = httpResource<PlanningDashboardMetrics>(() => {
    this.refreshTrigger();
    return this.enabledResources().planning ? `${this.basePath}/planning/metrics` : undefined;
  });

  /** Employee dashboard resource (self-service view) — gated by `employee` flag */
  private readonly employeeDashboardResource = httpResource<EmployeeDashboard>(() => {
    this.refreshTrigger();
    return this.enabledResources().employee ? `${this.basePath}/employee/my-shifts?weekStart=${this.employeeWeekStart()}` : undefined;
  });

  // Public Signals

  /** Dashboard statistics */
  readonly stats = computed(() => this.statsResource.value());

  /** Whether stats are loading */
  readonly statsLoading = () => this.statsResource.isLoading();

  /** Audit activity timeline data */
  readonly activityData = computed(() => this.activityResource.value()?.data ?? []);

  /** Total changes in the activity period */
  readonly activityTotal = computed(() => this.activityResource.value()?.total ?? 0);

  /** Whether activity data is loading */
  readonly activityLoading = () => this.activityResource.isLoading();

  /** Recent audit changes */
  readonly recentChanges = computed(() => this.recentChangesResource.value()?.data ?? []);

  /** Whether recent changes are loading */
  readonly recentChangesLoading = () => this.recentChangesResource.isLoading();

  /** Operation breakdown data */
  readonly operationBreakdown = computed(() => this.operationBreakdownResource.value() ?? []);

  /** Whether operation breakdown is loading */
  readonly operationBreakdownLoading = () => this.operationBreakdownResource.isLoading();

  /** Schema breakdown data */
  readonly schemaBreakdown = computed(() => this.schemaBreakdownResource.value() ?? []);

  /** Whether schema breakdown is loading */
  readonly schemaBreakdownLoading = () => this.schemaBreakdownResource.isLoading();

  /** Whether any audit data is currently loading */
  readonly isLoading = computed(
    () =>
      this.statsLoading() ||
      this.activityLoading() ||
      this.recentChangesLoading() ||
      this.operationBreakdownLoading() ||
      this.schemaBreakdownLoading()
  );

  // Planning Dashboard Signals

  /** Planning dashboard metrics */
  readonly planningMetrics = computed(() => this.planningMetricsResource.value());

  /** Whether planning metrics are loading */
  readonly planningMetricsLoading = () => this.planningMetricsResource.isLoading();

  /** Today's shifts list */
  readonly todayShifts = computed(() => this.planningMetricsResource.value()?.todayShifts ?? []);

  /** Hours trend (4 weeks) */
  readonly hoursTrend = computed(() => this.planningMetricsResource.value()?.hoursTrend ?? []);

  /** Weekly shift distribution */
  readonly weeklyDistribution = computed(
    () =>
      this.planningMetricsResource.value()?.weeklyDistribution ?? {
        labels: [],
        morning: [],
        afternoon: [],
        night: [],
      }
  );

  /** Coverage status segments */
  readonly coverageStatus = computed(() => this.planningMetricsResource.value()?.coverage ?? []);

  /** Planning dashboard summary stats */
  readonly planningSummary = computed(
    () =>
      this.planningMetricsResource.value()?.summary ?? {
        activeStaff: 0,
        shiftsToday: 0,
        hoursThisWeek: 0,
        coverageRate: 0,
      }
  );

  // Employee Dashboard Signals (self-service view)

  /** Employee dashboard data */
  readonly employeeDashboard = computed(() => this.employeeDashboardResource.value());

  /** Whether employee dashboard is loading */
  readonly employeeDashboardLoading = () => this.employeeDashboardResource.isLoading();

  /** Employee's next shift */
  readonly nextShift = computed(() => this.employeeDashboardResource.value()?.nextShift ?? null);

  /**
   * Optimistic patches applied on top of base shift data. Keyed by shift id.
   * Used to reflect resize/location changes immediately without a full reload
   * (which causes calendar flicker and scroll reset).
   */
  private readonly shiftOverridesOptimistic = signal<ReadonlyMap<string, Record<string, unknown>>>(new Map());

  /** Employee's shifts for the current week */
  readonly weekShifts = computed(() => {
    const base = this.employeeDashboardResource.value()?.weekShifts ?? [];
    const patches = this.shiftOverridesOptimistic();
    if (patches.size === 0) return base;
    return base.map(s => (patches.has(s.id) ? { ...s, ...patches.get(s.id) } : s));
  });

  /**
   * Apply an optimistic patch to a shift (e.g. updated override start/end/location).
   * The patch survives until the next reload() and is then replaced by fresh server data.
   */
  applyShiftOverride(shiftId: string, patch: Record<string, unknown>): void {
    const current = this.shiftOverridesOptimistic();
    const next = new Map(current);
    next.set(shiftId, { ...(current.get(shiftId) ?? {}), ...patch });
    this.shiftOverridesOptimistic.set(next);
  }

  /** Locations assigned to the employee (with validity windows) */
  readonly assignedLocations = computed(() => this.employeeDashboardResource.value()?.assignedLocations ?? []);

  /** Selected week start returned by the employee dashboard */
  readonly employeeDashboardWeekStart = computed(() => this.employeeDashboardResource.value()?.weekStart ?? this.employeeWeekStart());

  /** Employee's shift count this week */
  readonly shiftsThisWeek = computed(() => this.employeeDashboardResource.value()?.shiftsThisWeek ?? 0);

  /** Employee's hours this week */
  readonly hoursThisWeek = computed(() => this.employeeDashboardResource.value()?.hoursThisWeek ?? 0);

  /** Employee's name */
  readonly employeeName = computed(() => this.employeeDashboardResource.value()?.employeeName ?? '');

  /** Employee's team name */
  readonly employeeTeamName = computed(() => this.employeeDashboardResource.value()?.teamName ?? null);

  /** Employee's role name */
  readonly employeeRoleName = computed(() => this.employeeDashboardResource.value()?.roleName ?? null);

  // Public Methods

  /**
   * Refresh all dashboard data
   */
  reload(): void {
    this.shiftOverridesOptimistic.set(new Map());
    this.refreshTrigger.update(v => v + 1);
  }

  /**
   * Set the number of days for activity data
   */
  setActivityDays(days: number): void {
    this.activityDays.set(Math.max(1, Math.min(365, days)));
  }

  /**
   * Set the limit for recent changes
   */
  setRecentLimit(limit: number): void {
    this.recentLimit.set(Math.max(1, Math.min(100, limit)));
  }

  /**
   * Set the Monday week start used by employee dashboard widgets.
   */
  setEmployeeWeekStart(weekStart: string): void {
    this.employeeWeekStart.set(weekStart);
  }

  /**
   * Fetch the caller's primary team assignment in a single HTTP request.
   * Used by the employee "My Plan" route guard to resolve the canonical team
   * id for an employee who has not yet selected one in the URL.
   */
  fetchMyTeam(): Observable<{ teamId: string | null }> {
    return this.http.get<{ teamId: string | null }>(`${this.basePath}/employee/my-team`);
  }
}

function getWeekStartIso(date: Date): string {
  const monday = new Date(date);
  const currentDay = monday.getDay();
  monday.setDate(monday.getDate() - (currentDay === 0 ? 6 : currentDay - 1));
  monday.setHours(0, 0, 0, 0);
  const year = monday.getFullYear();
  const month = String(monday.getMonth() + 1).padStart(2, '0');
  const day = String(monday.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}
